import {
    ExternalLink,
    Facebook,
    Instagram,
    MapPin,
    Phone,
} from 'lucide-react';

import { COMPANY } from '@/lib/data';
import { trackEvent } from '@/lib/analytics';
import { usePublicContent } from '@/lib/usePublicContent';

const TikTokIcon = (props) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        {...props}
    >
        <path d="M14.5 3h2.6c.2 1.7 1.1 3.1 2.6 4 .9.5 1.9.8 3 .8v2.7c-1.9 0-3.7-.6-5.2-1.7v6.8c0 3.6-2.9 6.6-6.6 6.6S4.3 19.2 4.3 15.6c0-3.6 2.9-6.6 6.6-6.6.4 0 .8 0 1.2.1V12c-.4-.1-.8-.2-1.2-.2-2.1 0-3.8 1.7-3.8 3.8s1.7 3.8 3.8 3.8 3.8-1.7 3.8-3.8V3z" />
    </svg>
);

export const LocationContact = () => {
    const { settings = {}, content = {} } =
        usePublicContent();

    const address =
        settings.address || COMPANY.address;

    const mapsUrl =
        COMPANY.mapsShortUrl ||
        'https://maps.app.goo.gl/pqDoBEqysbpW8CzFA';

    const phone =
        settings.phone || COMPANY.phone;

    const instagramUrl =
        settings.instagram_url ||
        COMPANY.instagramUrl;

    const tiktokUrl =
        settings.tiktok_url ||
        COMPANY.tiktokUrl;

    const facebookUrl =
        settings.facebook_url ||
        COMPANY.facebookUrl;

    const title =
        content.contact_title ||
        'Visítanos en Pifo';

    const body =
        content.contact_body ||
        'Estamos listos para recibirte y ayudarte con la información que necesitas.';

    const onMap = () => {
        trackEvent(
            'map_click',
            window.location.pathname,
            {
                from: 'contact_page',
            }
        );
    };

    const onSocial = (platform) => {
        trackEvent(
            'social_click',
            window.location.pathname,
            {
                platform,
                from: 'contact_page',
            }
        );
    };

    const cleanPhone = String(phone || '').replace(
        /\s+/g,
        ''
    );

    return (
        <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
            <div className="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-[hsl(var(--kche-cyan))]/15 blur-3xl" />

            <div className="absolute -bottom-24 -right-20 h-96 w-96 rounded-full bg-[hsl(var(--kche-pink))]/10 blur-3xl" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-[2rem] border border-border bg-kche-soft p-6 shadow-soft sm:p-8 lg:p-10">
                        <div className="text-center">
                            <p className="text-sm font-bold font-display text-[hsl(var(--kche-pink))] uppercase tracking-wider">
                                Ubicación y contacto
                            </p>

                            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold">
                                {title}
                            </h2>

                            <p className="mx-auto mt-3 max-w-xl text-muted-foreground leading-relaxed">
                                {body}
                            </p>
                        </div>

                        <div className="mx-auto mt-8 max-w-xl space-y-5">
                            <div className="flex items-start gap-4 rounded-2xl bg-white/75 p-4">
                                <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))]">
                                    <MapPin className="size-5" />
                                </span>

                                <div className="min-w-0">
                                    <p className="font-display font-bold">
                                        Dirección
                                    </p>

                                    <a
                                        href={mapsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={onMap}
                                        className="mt-1 inline-flex items-start gap-2 text-sm text-muted-foreground transition-colors hover:text-[hsl(var(--kche-pink))]"
                                    >
                                        <span>
                                            {address}
                                        </span>

                                        <ExternalLink className="mt-0.5 size-4 shrink-0" />
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 rounded-2xl bg-white/75 p-4">
                                <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--kche-cyan))]/20 text-[hsl(var(--kche-blue))]">
                                    <Phone className="size-5" />
                                </span>

                                <div>
                                    <p className="font-display font-bold">
                                        Teléfono
                                    </p>

                                    <a
                                        href={`tel:${cleanPhone}`}
                                        className="mt-1 inline-block text-sm text-muted-foreground transition-colors hover:text-[hsl(var(--kche-blue))]"
                                    >
                                        {phone}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            {instagramUrl ? (
                                <a
                                    href={instagramUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() =>
                                        onSocial(
                                            'instagram'
                                        )
                                    }
                                    className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-pink))] px-5 py-2.5 text-sm font-bold font-display text-white shadow-soft transition-transform hover:scale-[1.03]"
                                >
                                    <Instagram className="size-4" />
                                    Instagram
                                </a>
                            ) : null}

                            {tiktokUrl ? (
                                <a
                                    href={tiktokUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() =>
                                        onSocial(
                                            'tiktok'
                                        )
                                    }
                                    className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-dark))] px-5 py-2.5 text-sm font-bold font-display text-white shadow-soft transition-transform hover:scale-[1.03]"
                                >
                                    <TikTokIcon className="size-4" />
                                    TikTok
                                </a>
                            ) : null}

                            {facebookUrl ? (
                                <a
                                    href={facebookUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() =>
                                        onSocial(
                                            'facebook'
                                        )
                                    }
                                    className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-blue))] px-5 py-2.5 text-sm font-bold font-display text-white shadow-soft transition-transform hover:scale-[1.03]"
                                >
                                    <Facebook className="size-4" />
                                    Facebook
                                </a>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LocationContact;